// Angular dependencies
import { Injectable } from '@angular/core';

import { LocalStorageService, SocketService as SocketServiceLegacy, ApiService } from '../core/upgraded-providers';
import SocketService from '../shared-services.module/socket.service';

import * as Screeps from '@screeps/map';

import {
    from,
    merge,
    Subject,
    Observable,
    Subscriber,
    Operator,
    BehaviorSubject
} from 'rxjs';
import {
    tap,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    takeUntil,
} from 'rxjs/operators';

@Injectable()
export class UnitsService {
    _socket: SocketService;

    _shard: string;
    set shard(shard) {
        this._shard = shard;
    }
    get shard() {
        return this._shard;
    }

    constructor(
        private _apiSrv: ApiService,
        private _socketLegacy: SocketServiceLegacy,
        private _localStorageSrv: LocalStorageService
    ) {
        const url = this._socketLegacy.options.websocketUrl;
        const token = this._localStorageSrv.get('auth', null, '');

        this._socket = new SocketService({ url, token });
    }

    asObservable() {
        return (source: Observable<any>): Observable<any> => {
            return source.lift(new UnitsOperator(
                this._socket,
                this._apiSrv.options.official ? this.shard : null
            ));
        };
    }

    setShard(shard) {
        this.shard = shard;
    }
}

class UnitsOperator<T, R> implements Operator<T, R> {
    constructor(
        private _socket: SocketService,
        private _shard: string
    ) {
    }

    call(subscriber: Subscriber<R>, source: any): any {
        return source.subscribe(new UnitsSubscriber(subscriber, this._socket, this._shard));
    }
}

class UnitsSubscriber<T, R> extends Subscriber<T> {
    _boundSbj: BehaviorSubject<any[]> = new BehaviorSubject([]);
    _completeSbj: Subject<void> = new Subject();

    constructor(
        destination: Subscriber<R>,
        private _socket: SocketService,
        private _shard: string
    ) {
        super(destination);

        const bound$ = this._boundSbj.asObservable()
            .pipe(startWith(null))
            .pipe(pairwise())
            .pipe(filter(([a, b]) => !Screeps.equal(a, b)));

        const onRooms$ = bound$
            .pipe(map(([prev, next]) => prev ? Screeps.subBoundRooms(next, prev) : Screeps.getRoomsInBound(next)))
            .pipe(filter((_: any[]) => !!_.length))
            .pipe(mergeMap((rooms: any[]) => from(rooms)))
            .pipe(mergeMap((room) => this._socketOn(room)))
            .pipe(filter(({ data: [ objects ] }) => Object.values(objects).some((changes: any[]) => !!changes.length)))
            .pipe(map<any, any>(({ data: [channel, objects] }) => [channel.match(/(W|E)\d+(N|S)\d+$/)[0], objects]))
            // .pipe(tap(([room]) => console.log(`changes in ${ room }`)))
            .pipe(tap((data: any) => destination.next(data)));

        const offRooms$ = bound$
            .pipe(map(([prev, next]) => prev ? Screeps.subBoundRooms(prev, next) : []))
            .pipe(filter((_: any[]) => !!_.length))
            .pipe(mergeMap((rooms: any[]) => from(rooms)))
            .pipe(tap((room) => this._socketOff(room)));

        merge(onRooms$, offRooms$)
            .pipe(takeUntil(this._completeSbj))
            .subscribe();
    }

    protected _next(bound: any) {
        this._boundSbj.next(bound);
    }

    protected _complete(): void {
        this._completeSbj.complete();

        const bound = this._boundSbj.getValue();
        const rooms = Screeps.getRoomsInBound(bound);

        for (const room of rooms) {
            this._socketOff(room);
        }

        this.destination.complete();
    }

    _socketOn(room: string): any {
        const url = this._shard ? `${ this._shard }/${ room }` : room;
        return this._socket.on(`roomMap2:${ url }`);
    }

    _socketOff(room: string): void {
        const url = this._shard ? `${ this._shard }/${ room }` : room;
        this._socket.off(`roomMap2:${ url }`);
    }
}
